import React from "react";
import {
  FieldValues,
  Path,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/common/DatePicker";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { copyToClipboard } from "@/utils/clipboard";
import { Files } from "lucide-react";
import { cn } from "@/lib/utils";

const FIELD_STYLES = {
  container: "space-y-2",
  label: "text-sm font-medium text-gray-700",
  errorText: "text-sm text-red-500",
  errorBorder: "border-red-500",
} as const;

interface FormFieldProps<T extends FieldValues> {
  id: Path<T>;
  label: string;
  required?: boolean;
  type?: string;
  placeholder?: string;
  register: UseFormRegister<T>;
  error?: string;
  accept?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  containerClassName?: string;
  className?: string;
  labelClassName?: string;
  contentClassName?: string;
  setValue?: UseFormSetValue<T>;
  watch?: UseFormWatch<T>;
  formatString?: string;
  maxDate?: Date;
  minDate?: Date;
  min?: number | string;
  isEdit?: boolean;
  isCopy?: boolean;
  value?: string | number;
  disabled?: boolean;
  rightElement?: React.ReactNode;
  buttonClassName?: string;
}

interface SelectOption {
  value: string;
  label: string;
}

interface SelectFieldProps<T extends FieldValues> {
  id: Path<T>;
  label: string;
  required?: boolean;
  placeholder?: string;
  options: SelectOption[];
  value?: string;
  onValueChange?: (value: any) => void;
  error?: string;
  containerClassName?: string;
  className?: string;
  isEdit?: boolean;
  register: UseFormRegister<T>;
}

export const NonEditableFormField = ({
  value,
  label,
  labelClassName,
  contentClassName,
  valueClassName,
  buttonClassName,
  containerClassName,
  isCopy,
}: {
  value: string | number;
  label?: string;
  labelClassName?: string;
  contentClassName?: string;
  valueClassName?: string;
  buttonClassName?: string;
  containerClassName?: string;
  isCopy: boolean;
}) => {
  const handleCopy = async (text: string) => {
    await copyToClipboard(text);
  };

  const displayValue = value === 0 ? "0" : (value || "").toString();

  return (
    <div className={cn(FIELD_STYLES.container, containerClassName)}>
      {label && (
        <label className={cn(FIELD_STYLES.label, labelClassName)}>
          {label}
        </label>
      )}
      <div
        className={cn(
          "flex border-b-2 border-gray-200 py-2 text-lg",
          contentClassName,
        )}
      >
        <span className={cn("overflow-hidden text-ellipsis", valueClassName)}>
          {displayValue}
        </span>

        {isCopy && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleCopy(value.toString()!)}
            className={cn("ml-auto", buttonClassName)}
          >
            <Files className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export const FormField = <T extends FieldValues>({
  id,
  label,
  required = false,
  type = "text",
  placeholder,
  register,
  error,
  accept,
  onChange,
  className,
  labelClassName,
  contentClassName,
  containerClassName,
  setValue,
  watch,
  minDate,
  maxDate,
  min = undefined,
  formatString = "dd/MM/yyyy",
  isEdit = true,
  isCopy = false,
  disabled = false,
  onKeyDown,
  value,
  rightElement,
  buttonClassName,
}: FormFieldProps<T>) => {
  if (!isEdit) {
    return (
      <NonEditableFormField
        isCopy={isCopy}
        value={value!}
        label={label}
        labelClassName={labelClassName}
        contentClassName={contentClassName}
        buttonClassName={buttonClassName}
      />
    );
  }

  const dateValue = watch ? watch(id) : undefined;

  if (type === "date" && setValue && watch) {
    return (
      <div className={cn(FIELD_STYLES.container, containerClassName)}>
        <label htmlFor={id} className={FIELD_STYLES.label}>
          {label} {required && "*"}
        </label>
        <DatePicker
          className={`min-w-full text-left ${error ? FIELD_STYLES.errorBorder : ""} ${className || ""}`}
          placeholder={placeholder}
          date={dateValue ? new Date(dateValue) : null}
          onDateChange={(date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            setValue(id, `${year}-${month}-${day}` as any);
          }}
          minDate={minDate}
          maxDate={maxDate}
          formatString={formatString}
        />
        {error && <p className={FIELD_STYLES.errorText}>{error}</p>}
      </div>
    );
  }

  // Capture the registration so we can compose react-hook-form's own onChange
  // with an optional caller-provided one. Assigning `onChange={onChange}` after
  // `{...register(id)}` would otherwise overwrite RHF's handler — when no custom
  // onChange is passed it becomes `undefined`, so RHF never sees keystrokes and
  // the field never becomes dirty (breaking form-dirty gating like Save buttons).
  const field = register(id);

  return (
    <div className={cn(FIELD_STYLES.container, containerClassName)}>
      <label htmlFor={id} className={cn(FIELD_STYLES.label, labelClassName)}>
        {label} {required && "*"}
      </label>
      <div className="relative flex items-center">
        <Input
          id={id}
          type={type}
          {...field}
          placeholder={placeholder}
          className={`${error ? FIELD_STYLES.errorBorder : ""} ${className || ""}`}
          accept={accept}
          onChange={(event) => {
            field.onChange(event);
            onChange?.(event);
          }}
          onKeyDown={onKeyDown}
          disabled={disabled}
          min={min}
        />
        {rightElement}
      </div>
      {error && <p className={FIELD_STYLES.errorText}>{error}</p>}
    </div>
  );
};

export const SelectField = <T extends FieldValues>({
  label,
  required = false,
  placeholder,
  options,
  value,
  onValueChange,
  error,
  containerClassName,
  className,
  isEdit = true,
}: SelectFieldProps<T>) => {
  if (!isEdit) {
    const selectedOption = options.find((option) => option.value === value);
    return (
      <NonEditableFormField
        isCopy={false}
        value={selectedOption?.label || value || ""}
        label={label}
      />
    );
  }

  return (
    <div className={cn(FIELD_STYLES.container, containerClassName)}>
      <label className={FIELD_STYLES.label}>
        {label} {required && "*"}
      </label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger
          className={cn(
            "h-10 w-full",
            error && FIELD_STYLES.errorBorder,
            className,
          )}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="w-full">
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && <p className={FIELD_STYLES.errorText}>{error}</p>}
    </div>
  );
};
